import { useState, useEffect } from 'react'
import { Globe, Menu as MenuIcon } from 'lucide-react'
import { useLanguage, commonTranslations } from '@/hooks/useLanguage'
import { useConfig } from '@/hooks/useMenuData'
import { getTextAlign, getIconTransform } from '@/lib/rtl'
import type { Language, Config } from '@/types'

// ============================================================================
// Types
// ============================================================================

interface HeaderProps {
  onMenuClick?: () => void
  showMobileMenu?: boolean
}

// ============================================================================
// Language Toggle Component
// ============================================================================

interface LanguageToggleProps {
  currentLanguage: Language
  onLanguageChange: (lang: Language) => void
  availableLanguages: Language[]
  isRTL: boolean
}

function LanguageToggle({
  currentLanguage,
  onLanguageChange,
  availableLanguages,
  isRTL
}: LanguageToggleProps) {
  const [isOpen, setIsOpen] = useState(false)

  const languageNames: Record<Language, string> = {
    fr: 'FR',
    ar: 'AR',
    en: 'EN'
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/80 hover:bg-white transition-colors touch-feedback"
        aria-label="Change language"
      >
        <Globe size={18} className="text-primary" />
        <span className="font-medium text-sm">{languageNames[currentLanguage]}</span>
      </button>

      {isOpen && (
        <div
          className={`absolute top-full mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-50 animate-slide-up min-w-[120px] ${
            isRTL ? 'left-0' : 'right-0'
          }`}
        >
          {availableLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => {
                onLanguageChange(lang)
                setIsOpen(false)
              }}
              className={`w-full px-4 py-2 text-left hover:bg-secondary/20 transition-colors ${
                lang === currentLanguage ? 'bg-secondary/40 font-medium' : ''
              }`}
            >
              {languageNames[lang]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Header Component
// ============================================================================

export function Header({ onMenuClick, showMobileMenu = true }: HeaderProps) {
  const { language, setLanguage, isRTL, textDirection } = useLanguage()
  const config = useConfig()
  const [scrolled, setScrolled] = useState(false)

  // Only track scroll for subtle shadow — header always stays visible
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!config) return null

  const { business, features } = config
  const availableLanguages = features.languageToggle
    ? (config.languages.enabled || ['fr', 'ar', 'en'])
    : [language]

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-shadow duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-white/90 backdrop-blur-sm'
      }`}
      dir={textDirection}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo and Business Name */}
          <div
            className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {business.logo && (
              <img
                src={business.logo}
                alt={business.name}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl object-cover ring-1 ring-border shadow-sm"
              />
            )}
            <div className={getTextAlign(language)}>
              <h1 className="text-lg sm:text-2xl font-bold text-primary leading-tight line-clamp-1">
                {business.name}
              </h1>
            </div>
          </div>

          {/* Right Side Actions */}
          <div className={`flex items-center gap-2 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Language Toggle */}
            {features.languageToggle && availableLanguages.length > 1 && (
              <LanguageToggle
                currentLanguage={language}
                onLanguageChange={setLanguage}
                availableLanguages={availableLanguages}
                isRTL={isRTL}
              />
            )}

            {/* Mobile Menu Button */}
            {showMobileMenu && onMenuClick && (
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 rounded-lg hover:bg-secondary/20 transition-colors touch-feedback"
                aria-label="Open menu"
                style={{
                  transform: getIconTransform(language, 'nav')
                }}
              >
                <MenuIcon size={24} className="text-primary" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Border Animation */}
      <div
        className={`h-0.5 bg-gradient-to-r from-primary via-accent to-primary transition-all duration-500 ${
          scrolled ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </header>
  )
}

// ============================================================================
// Spacer for Fixed Header
// ============================================================================

export function HeaderSpacer() {
  return <div className="h-16 sm:h-20" />
}
